<!--
  Démo d'ergonomie : reprend le shell plein écran du vrai test
  (sidebar progression + zone centrale + panneau assistant droit) afin que
  le candidat retrouve exactement la même interface pendant l'examen.
-->
<div class="demo-overlay fixed inset-0 z-[60] flex flex-col bg-white">

    <!-- ── Bandeau démo permanent (toujours visible) ── -->
    <div class="demo-banner flex items-center justify-between gap-3 px-4 py-2.5
                bg-amber-50 border-b border-amber-200 shrink-0">
        <div class="flex items-center gap-2 text-amber-800">
            <mat-icon class="icon-size-5">science</mat-icon>
            <span class="text-sm font-medium" translate>ai-exam-demo.banner</span>
        </div>
        <button mat-stroked-button type="button" (click)="skipDemo()">
            <span translate>ai-exam-demo.skip</span>
        </button>
    </div>

    <!-- ══════ INTRO ══════ -->
    @if (stage() === 'intro') {
    <div class="flex-auto overflow-y-auto">
        <div class="max-w-xl mx-auto text-center px-4 py-10">
            <mat-icon class="icon-size-12 text-lilate mb-4">tour</mat-icon>
            <h2 class="font-dm-serif text-[28px] text-lilate-ink mb-3" translate>ai-exam-demo.intro.title</h2>
            <p class="font-dm-sans text-lilate-ink-soft leading-relaxed mb-6" translate>ai-exam-demo.intro.text</p>

            <ul class="inline-block text-left text-lilate-ink-soft space-y-2 mb-8">
                <li class="flex items-center gap-2">
                    <mat-icon class="icon-size-5 text-lilate">check_circle</mat-icon>
                    <span translate>ai-exam-demo.intro.point-duration</span>
                </li>
                <li class="flex items-center gap-2">
                    <mat-icon class="icon-size-5 text-lilate">check_circle</mat-icon>
                    <span translate>ai-exam-demo.intro.point-not-evaluated</span>
                </li>
                <li class="flex items-center gap-2">
                    <mat-icon class="icon-size-5 text-lilate">check_circle</mat-icon>
                    <span translate>ai-exam-demo.intro.point-preparing</span>
                </li>
            </ul>

            <div class="flex justify-center">
                <button mat-flat-button type="button" (click)="startDemo()"
                        class="font-dm-sans font-semibold text-base- !rounded-lilate !py-3.5 !px-8 h-auto min-h-0
                               !bg-lilate !text-white border-none hover:!bg-lilate-dark">
                    <span translate>ai-exam-demo.intro.start</span>
                </button>
            </div>
        </div>
    </div>
    }

    <!-- ══════ EXERCICE FACTICE (shell 3 colonnes du vrai test) ══════ -->
    @if (stage() === 'exercise') {

    <div class="flex flex-row flex-auto min-h-0 overflow-hidden">

        <!-- ═══ Colonne 1 — Sidebar progression (masquée en mobile) ═══ -->
        <aside class="demo-sidebar hidden lg:flex flex-col shrink-0">

            <!-- Brand -->
            <div class="demo-sb-brand">
                @if (testType === 'lilate') {
                <img class="h-5 w-auto block"
                     src="https://cpformation.com/wp-content/uploads/2026/03/LOGO-LILATE-AI-1.png" alt="LILATE AI">
                } @else {
                <img class="w-32" src="assets/images/brand/logo-FlashLevel-bg.png" alt="FlashLevel">
                }
            </div>

            <!-- Durée du test -->
            <div class="demo-sb-section flex items-center gap-1.5 text-lilate-ink-muted">
                <mat-icon class="icon-size-5">schedule</mat-icon>
                <span class="font-dm-sans text-sm" translate>ai-exam-demo.sidebar.duration</span>
            </div>

            <!-- Progression -->
            <div class="demo-sb-section demo-sb-steps">
                <div class="demo-sb-label" translate>ai-exam-demo.sidebar.progression</div>

                <div class="flex flex-col relative">
                    <!-- Ligne verticale de connexion -->
                    <div class="absolute left-[13px] top-4 bottom-4 w-px bg-gray-200"></div>

                    @for (activityKey of sidebarActivities; track activityKey; let i = $index) {
                    <div class="grid grid-cols-[28px_1fr] gap-2.5 items-start relative z-[1] py-[5px]">
                        <!-- Dot -->
                        <div class="w-7 h-7 rounded-lilate-sm border flex items-center justify-center
                                    text-xs font-semibold shrink-0"
                             [ngClass]="i === 0
                                ? 'bg-white border-lilate text-lilate'
                                : 'bg-white/80 border-gray-200 text-lilate-ink-faint'">
                            {{ i + 1 }}
                        </div>
                        <!-- Nom + statut -->
                        <div>
                            <div class="text-sm+ font-medium leading-tight pt-1.5"
                                 [ngClass]="i === 0 ? 'text-lilate-ink font-semibold' : 'text-lilate-ink-soft'"
                                 translate>{{ activityKey }}</div>
                            @if (i === 0) {
                            <div class="text-xs+ mt-px text-lilate font-medium" translate>
                                ai-exam-demo.sidebar.in-progress
                            </div>
                            }
                        </div>
                    </div>
                    }
                </div>
            </div>
        </aside>

        <!-- ═══ Colonne 2 + 3 — Zone centrale + panneau assistant ═══ -->
        <div class="flex flex-row flex-auto min-w-0 min-h-0">

            <!-- ── Zone centrale ── -->
            <div class="demo-center-zone flex-1 flex flex-col bg-white overflow-hidden min-h-0">

                <!-- ── Slide body (scrollable) ── -->
                <div class="demo-slide-body flex-auto overflow-y-auto pb-4">

                    <!-- Zone question -->
                    <div [ngClass]="zoneClass('question')">
                        <div class="font-dm-serif text-[28px] leading-[1.2] text-lilate-ink mb-5 tracking-[-0.3px]
                                    max-sm:text-[22px]">
                            {{ ('ai-exam-demo.' + exerciseKind() + '.title') | translate }}
                        </div>
                        <div class="font-dm-sans text-base+ text-lilate-ink-soft leading-[1.75] mb-2.5">
                            {{ ('ai-exam-demo.' + exerciseKind() + '.context') | translate }}
                        </div>
                        <div class="font-dm-sans text-base+ text-lilate-ink-soft leading-[1.75] mb-2.5">
                            {{ ('ai-exam-demo.' + exerciseKind() + '.instructions') | translate }}
                        </div>

                        <!-- Icônes reformuler / écouter (décoratives en démo) -->
                        <div class="flex items-center gap-1 mt-1 text-gray-400">
                            <mat-icon class="icon-size-6" aria-hidden="true">autorenew</mat-icon>
                            <mat-icon class="icon-size-6" aria-hidden="true">volume_up</mat-icon>
                        </div>
                    </div>

                    <hr class="border-0 border-t border-gray-200 my-6">

                    <!-- Zone document / média -->
                    <div [ngClass]="zoneClass('media')">

                        <!-- Exercice 1 : document texte -->
                        @if (exerciseKind() === 'choice') {
                        <div class="font-dm-sans text-[10px] font-semibold tracking-wider uppercase
                                    text-lilate-ink-faint mb-3.5" translate>ai-exam-demo.media-label</div>
                        <div class="bg-[#fafafa] border border-gray-200 rounded-lilate border-l-[3px] border-l-lilate
                                    px-7 py-6 font-dm-sans text-base- leading-[1.8] text-lilate-ink-soft italic">
                            {{ 'ai-exam-demo.choice.media' | translate }}
                        </div>
                        }

                        <!-- Exercice 2 : lecteur audio contrôlé (texte jamais affiché) -->
                        @if (exerciseKind() === 'listening') {
                        <div class="font-dm-sans text-[10px] font-semibold tracking-wider uppercase
                                    text-lilate-ink-faint mb-3.5" translate>ai-exam-demo.media-label</div>
                        <div class="demo-listening-player bg-[#fafafa] border border-gray-200 rounded-lilate px-7 py-6">
                            <div class="flex flex-col items-center gap-3">
                                <button type="button" class="demo-listening-play-btn" [class.is-playing]="isPlaying()"
                                        [disabled]="!canInteract() || isPlaying() || playsLeft() <= 0"
                                        (click)="playListening()">
                                    <mat-icon class="!w-7 !h-7 text-white"
                                              [class.demo-listening-pulse]="isPlaying()">
                                        {{ isPlaying() ? 'graphic_eq' : 'play_arrow' }}
                                    </mat-icon>
                                </button>
                                <div class="font-dm-sans text-base- text-lilate-ink-soft text-center" translate>
                                    ai-exam-demo.listen-button
                                </div>
                                <div class="font-dm-sans text-[12px] text-lilate-ink-faint">
                                    {{ playsLeft() }} {{ 'ai-exam-demo.plays-left' | translate }}
                                </div>
                                @if (playProgress() > 0) {
                                <div class="w-full max-w-80 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                    <div class="h-full bg-lilate rounded-full transition-all duration-200"
                                         [style.width.%]="playProgress()"></div>
                                </div>
                                }
                                <p class="font-dm-sans text-[12px] text-lilate-ink-faint mt-1" translate>
                                    ai-exam-demo.listening.no-transcript-hint
                                </p>
                            </div>
                        </div>
                        }

                        <!-- Exercice 3 : pas de document -->
                        @if (exerciseKind() === 'speaking') {
                        <div class="bg-[#fafafa] border border-dashed border-gray-300 rounded-lilate px-7 py-6
                                    text-center font-dm-sans text-base- text-lilate-ink-faint" translate>
                            ai-exam-demo.speaking.no-media
                        </div>
                        }
                    </div>

                </div>

                <!-- ── Response bar (sticky en bas) ── -->
                <div class="demo-response-bar shrink-0 z-30 bg-white border-t border-gray-200
                            shadow-[0_-4px_24px_rgba(0,0,0,0.07)] pt-3.5 pb-4 max-h-[50vh] overflow-y-auto">

                    <!-- Zone réponse -->
                    <div [ngClass]="zoneClass('answer')">

                        <!-- QCU (exercices 1 et 2) -->
                        @if (exerciseKind() === 'choice' || exerciseKind() === 'listening') {
                        <div class="flex flex-col gap-2">
                            <div class="flex items-center gap-1.5 mb-1">
                                <span class="text-[10.5px] font-bold uppercase tracking-wide text-gray-400"
                                      translate>ai-choice.label-single</span>
                            </div>

                            @for (choiceIndex of [1, 2, 3]; track choiceIndex) {
                            <div class="group flex items-start gap-3 py-3 px-3.5 border-[1.5px] rounded-[11px]
                                        text-[13.5px] leading-normal cursor-pointer select-none
                                        transition-all duration-[140ms] ease-in-out"
                                 [ngClass]="{
                                     'border-gray-200 bg-white text-gray-500 hover:border-[#a7d9c8] hover:bg-[#f7fdfb] hover:text-gray-800': selectedChoice() !== choiceIndex,
                                     'border-lilate bg-white text-gray-800 shadow-[0_0_0_3px_rgba(29,158,117,0.1)]': selectedChoice() === choiceIndex
                                 }"
                                 (click)="selectChoice(choiceIndex)">
                                <span class="w-5 h-5 shrink-0 rounded-full border-2 mt-[1px]
                                             flex items-center justify-center transition-all duration-[140ms]"
                                      [ngClass]="{
                                          'border-gray-200 bg-white group-hover:border-[#a7d9c8]': selectedChoice() !== choiceIndex,
                                          'border-lilate bg-lilate': selectedChoice() === choiceIndex
                                      }">
                                    @if (selectedChoice() === choiceIndex) {
                                    <span class="w-1.5 h-1.5 rounded-full bg-white block"></span>
                                    }
                                </span>
                                <span class="flex-1 pt-px">
                                    {{ ('ai-exam-demo.' + exerciseKind() + '.choice' + choiceIndex) | translate }}
                                </span>
                            </div>
                            }
                        </div>
                        }

                        <!-- Oral (exercice 3) -->
                        @if (exerciseKind() === 'speaking') {
                        <div class="demo-recorder">
                            @if (recordingState() === 'idle') {
                            <div class="flex items-center gap-4 bg-[#f9fafb] border border-gray-200 rounded-xl py-2.5 px-4">
                                <button type="button"
                                        class="flex-1 min-h-[44px] bg-transparent border-none flex items-center
                                               cursor-pointer p-0 disabled:opacity-45 disabled:cursor-not-allowed"
                                        [disabled]="!canInteract()" (click)="startRecording()">
                                    <span class="group inline-flex items-center gap-2 font-dm-sans text-[13.5px]
                                                 text-gray-400 transition-colors duration-150 hover:text-gray-500">
                                        <mat-icon class="!w-[18px] !h-[18px]">mic</mat-icon>
                                        <span translate>ai-exam-demo.record-start</span>
                                    </span>
                                </button>
                            </div>
                            }

                            @if (recordingState() === 'recording') {
                            <div class="flex items-center gap-4 bg-[#f9fafb] border border-gray-200 rounded-xl py-2.5 px-4">
                                <button type="button"
                                        class="flex-1 min-h-[44px] bg-transparent border-none flex items-center cursor-pointer p-0"
                                        (click)="stopRecording()">
                                    <span class="flex items-center gap-[3px] h-9 flex-1">
                                        <span class="demo-wl-bar"></span>
                                        <span class="demo-wl-bar"></span>
                                        <span class="demo-wl-bar"></span>
                                        <span class="demo-wl-bar"></span>
                                        <span class="demo-wl-bar"></span>
                                        <span class="ml-4 font-dm-sans text-[13.5px] text-gray-400" translate>
                                            ai-exam-demo.record-stop
                                        </span>
                                    </span>
                                </button>
                                <div class="flex flex-col gap-0.5 shrink-0 text-right">
                                    <span class="font-dm-sans text-[10px] font-semibold text-gray-400 uppercase tracking-wide"
                                          translate>audio-recorder.max-recording-time</span>
                                    <span class="font-dm-sans text-[13.5px] text-gray-500">{{ recordSeconds() }}s</span>
                                </div>
                            </div>
                            }

                            @if (recordingState() === 'recorded') {
                            <div class="flex flex-col gap-2">
                                <audio [src]="recordedAudioUrl()" controls class="w-full h-9 rounded-lg"></audio>
                                <button type="button"
                                        class="self-start font-dm-sans text-sm text-lilate-ink-muted underline
                                               underline-offset-2 hover:text-lilate-ink-soft"
                                        (click)="resetRecording()">
                                    <span translate>ai-exam-demo.record-again</span>
                                </button>
                            </div>
                            }

                            @if (recordingState() === 'error') {
                            <div class="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700"
                                 translate>ai-exam-demo.record-error</div>
                            }
                        </div>
                        }
                    </div>

                    <!-- Invite « à vous de jouer » -->
                    @if (canInteract()) {
                    <p class="text-center font-dm-sans text-[13px] text-lilate-ink-muted mt-4" translate>
                        ai-exam-demo.try-hint
                    </p>
                    }

                    <!-- Zone actions -->
                    <div [ngClass]="zoneClass('actions')"
                         class="flex items-center justify-between gap-3 mt-4">
                        <button mat-flat-button type="button"
                                class="font-dm-sans font-semibold text-base- !rounded-lilate !py-3.5 !px-6 h-auto min-h-0
                                       !bg-lilate !text-white border-none transition-all duration-150
                                       hover:!bg-lilate-dark disabled:opacity-45 disabled:cursor-not-allowed"
                                [disabled]="!canValidate()" (click)="validateAnswer()">
                            <span translate>ai-exam-demo.submit</span>
                        </button>

                        @if (analysisInProgress()) {
                        <div class="flex items-center gap-2.5">
                            <mat-spinner [diameter]="24"></mat-spinner>
                            <span class="font-dm-sans text-[13px] text-lilate-ink-muted" translate>
                                ai-exam-demo.analysis
                            </span>
                        </div>
                        }

                        <button type="button"
                                class="font-dm-sans font-medium text-[13px] text-lilate-ink-muted bg-transparent
                                       border border-gray-200 cursor-pointer px-3 py-1.5 rounded-md
                                       transition-all duration-150 hover:text-lilate-ink-soft hover:bg-gray-100
                                       hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                [disabled]="!canInteract()" (click)="skipExercise()">
                            <span translate>ai-exam-demo.no-answer</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- ── Panneau assistant droit (chrono circulaire + critères) ── -->
            <div class="demo-panel-wrap shrink-0" [ngClass]="zoneClass('timer')">
                <lib-exam-assistant-panel
                    [activityLabel]="activityLabelKey() | translate"
                    [remainingSeconds]="remainingSeconds()"
                    [totalSeconds]="totalTimerSeconds"
                    [evaluationCriteria]="criteriaKey() | translate"
                    [showTimer]="true"
                    [showAbandon]="false"
                    [collapsed]="rightPanelCollapsed()"
                    (collapsedChange)="rightPanelCollapsed.set($event)">
                </lib-exam-assistant-panel>
            </div>

        </div>
    </div>

    <!-- ── Carte du tour guidé (barre basse) ── -->
    @if (activeTourZone(); as zone) {
    <div class="demo-tour-bar shrink-0 bg-lilate text-white">
        <div class="max-w-3xl mx-auto px-6 py-4 flex items-start justify-between gap-4">
            <div>
                <div class="text-xs font-semibold uppercase tracking-wide opacity-80 mb-1">
                    {{ tourZoneIndex() + 1 }} / {{ tourZones.length }}
                </div>
                <h3 class="font-dm-serif text-lg mb-1">
                    {{ ('ai-exam-demo.tour.' + zone + '.title') | translate }}
                </h3>
                <p class="font-dm-sans text-sm leading-relaxed opacity-90">
                    {{ ('ai-exam-demo.tour.' + zone + '.text') | translate }}
                </p>
            </div>
            <button mat-flat-button type="button" class="shrink-0 !bg-white !text-lilate-dark font-semibold"
                    (click)="nextTourZone()">
                <span translate>
                    {{ tourZoneIndex() === tourZones.length - 1 ? 'ai-exam-demo.tour.try' : 'ai-exam-demo.tour.next' }}
                </span>
            </button>
        </div>
    </div>
    } @else if (feedbackKey()) {
    <!-- ── Encart de transition après chaque exercice (barre basse) ── -->
    <div class="demo-feedback shrink-0 border-t px-6 py-4"
         [ngClass]="timeoutReached() ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'">
        <div class="max-w-3xl mx-auto flex items-start gap-3">
            <mat-icon class="icon-size-6 shrink-0"
                      [ngClass]="timeoutReached() ? 'text-amber-600' : 'text-green-600'">
                {{ timeoutReached() ? 'hourglass_bottom' : 'check_circle' }}
            </mat-icon>
            <div class="flex-1">
                <p class="font-dm-sans text-lilate-ink-soft leading-relaxed mb-3" translate>{{ feedbackKey() }}</p>
                <button mat-flat-button type="button"
                        class="font-dm-sans font-semibold text-base- !rounded-lilate !py-3 !px-6 h-auto min-h-0
                               !bg-lilate !text-white border-none hover:!bg-lilate-dark"
                        (click)="continueToNext()">
                    <span translate>ai-exam-demo.continue</span>
                </button>
            </div>
        </div>
    </div>
    }
    }

    <!-- ══════ RÉCAPITULATIF FINAL ══════ -->
    @if (stage() === 'done') {
    <div class="flex-auto overflow-y-auto">
        <div class="max-w-xl mx-auto text-center px-4 py-10">
            <mat-icon class="icon-size-12 text-lilate mb-4">verified</mat-icon>
            <h2 class="font-dm-serif text-[28px] text-lilate-ink mb-6" translate>ai-exam-demo.done.title</h2>

            <ul class="inline-block text-left text-lilate-ink-soft space-y-3 mb-8">
                @for (pointIndex of [1, 2, 3, 4]; track pointIndex) {
                <li class="flex items-start gap-2.5">
                    <mat-icon class="icon-size-5 text-lilate shrink-0 mt-0.5">check_circle</mat-icon>
                    <span translate>{{ 'ai-exam-demo.done.point' + pointIndex }}</span>
                </li>
                }
            </ul>

            <div class="flex justify-center">
                <button mat-flat-button type="button" (click)="skipDemo()"
                        class="font-dm-sans font-semibold text-base- !rounded-lilate !py-3.5 !px-8 h-auto min-h-0
                               !bg-lilate !text-white border-none hover:!bg-lilate-dark">
                    <span translate>ai-exam-demo.finish</span>
                </button>
            </div>
        </div>
    </div>
    }
</div>
